import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import type { FlowDirection } from './useStepFlow';

type WizardShellProps = {
  stepKey: string;
  position: number;
  total: number;
  direction: FlowDirection;
  leaving: boolean;
  onBack?: () => void;
  preview: ReactNode;
  finished?: boolean;
  /** The independent signup, labelled as such next to the brand. */
  solo?: boolean;
  children: ReactNode;
};

/**
 * The frame every question sits in: the brand, a hairline of progress, the
 * way back, the question itself, and (on wide screens) the business card
 * building itself on the right as the answers come in.
 */
export function WizardShell({ stepKey, position, total, direction, leaving, onBack, preview, finished, solo, children }: WizardShellProps) {
  const progress = finished ? 1 : position / total;
  return (
    <main className="wiz" data-finished={finished ? 'true' : undefined}>
      <header className="wiz-top">
        <p className="brand" aria-hidden="true">
          <span className="brand-word">
            Dale<span className="dot">.</span>
          </span>
          <span className="brand-sub">{solo ? 'Independientes' : 'Negocios'}</span>
        </p>
        <p className="wiz-top-alt">
          ¿Ya tienes cuenta? <Link to="/entrar">Entra</Link>
        </p>
        <div
          className="wiz-progress"
          role="progressbar"
          aria-label="Avance del registro"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={finished ? total : position}
        >
          <span className="wiz-progress-fill" style={{ transform: `scaleX(${progress})` }} />
        </div>
      </header>
      <div className="wiz-body">
        <section className="wiz-stage">
          {onBack && !finished ? (
            <button type="button" className="wiz-back" onClick={onBack}>
              <svg viewBox="0 0 20 20" aria-hidden="true" width="18" height="18">
                <path d="M12.5 4.5 7 10l5.5 5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Atrás
            </button>
          ) : null}
          <div key={stepKey} className="wiz-step" data-direction={direction} data-leaving={leaving ? 'true' : undefined}>
            {children}
          </div>
        </section>
        <aside className="wiz-preview" aria-label="Así te verán en Dale">
          {preview}
        </aside>
      </div>
    </main>
  );
}
